import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'components';
import {
  Product,
  Price,
} from 'pages/Settings/pages/Add-subscription/types/Add-subscription.types';
import {
  IProductDetails,
  addProductsToSubscription,
  AddProductsRequest,
} from 'shared/services/user-subscription';
import FormCouponPrice from 'components/organisms/Form-cupon-price/Form-cupon-price';
import { useFormCuponPrice } from 'components/organisms/Form-cupon-price/hooks/Form-cupon-price.hook';
import { calculateProrrate, formatCurrency } from 'utils/prorrate.utils';
import { useSelector } from 'react-redux';
import { IAppReducer } from 'models';
import './AddonSelectionModal.scss';

interface AddonSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedAddons: SelectedAddon[]) => void;
  onError?: (error: string) => void;
  productsDetails: IProductDetails[];
  period: 'Mensual' | 'Anual';
  isLoading?: boolean;
  dateExpire: string;
  typePeriodicity: 'MENSUAL' | 'ANUAL';
  subId: string;
  currentPlanPrice: number;
  products: Product[];
  selectedProductQuantity: number;
  selectedProductType: string;
  selectedUserProductId: number;
}

interface SelectedAddon {
  priceId: string;
  productId: number;
  productName: string;
  price: number;
  additionalPrice: number;
  maxAdditional: number;
  quantity: number;
  productType: string;
}

interface AvailableAddon {
  product: Product;
  price: Price;
  quantity: number;
}

export const AddonSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  onError,
  productsDetails,
  period,
  isLoading = false,
  dateExpire,
  typePeriodicity,
  subId,
  currentPlanPrice,
  products,
  selectedProductQuantity,
  selectedProductType,
  selectedUserProductId,
}: AddonSelectionModalProps) => {
  const [availableAddons, setAvailableAddons] = useState<AvailableAddon[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponNotApplicableError, setCouponNotApplicableError] =
    useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Obtener authId del store
  const { authReducer: user } = useSelector((state: IAppReducer) => state);

  // Hook para manejo de cupones
  const {
    couponError,
    appliedCoupon,
    couponCode,
    loadingCoupon,
    resetCoupon,
    handleCouponCode,
    handleApplyCoupon: originalHandleApplyCoupon,
  } = useFormCuponPrice();

  // Función personalizada para aplicar cupón con validación de addons
  const handleApplyCoupon = async () => {
    // Limpiar errores previos
    setCouponNotApplicableError(null);

    // Verificar si hay addons seleccionados antes de aplicar el cupón
    if (selectedAddons.length === 0) {
      setCouponNotApplicableError(
        'Primero selecciona un addon para aplicar el cupón'
      );
      return;
    }

    await originalHandleApplyCoupon();
  };

  // Efecto para validar si el cupón aplica para algún addon
  useEffect(() => {
    if (
      appliedCoupon &&
      (availableAddons.length > 0 || selectedAddons.length > 0)
    ) {
      if (!isCouponApplicableToAnyAddon()) {
        setCouponNotApplicableError(
          'El cupón no aplica para los addons seleccionados'
        );
      } else {
        setCouponNotApplicableError(null);
      }
    } else if (!appliedCoupon) {
      // Limpiar error solo cuando no hay cupón aplicado
      setCouponNotApplicableError(null);
    }
  }, [appliedCoupon, availableAddons, selectedAddons, resetCoupon]);

  // Resetear cupón y addons cuando se abre el modal por primera vez
  useEffect(() => {
    if (isOpen) {
      resetCoupon();
      setCouponNotApplicableError(null);
      setSelectedAddons([]);
      setSubmitError(null);
    }
  }, [isOpen]);

  const getAvailableAddonTypes = () => {
    // Solo devolver el tipo del producto seleccionado
    return selectedProductType ? [selectedProductType] : [];
  };

  const getBaseProductQuantity = () => {
    // Usar directamente la cantidad del producto seleccionado
    return selectedProductQuantity;
  };

  useEffect(() => {
    if (!isOpen || !products || products.length === 0) return;

    const loadAddons = () => {
      setLoading(true);
      setError(null);
      try {
        const availableTypes = getAvailableAddonTypes();
        const addons: AvailableAddon[] = [];

        products.forEach((product) => {
          if (product.is_addon) {
            product.prices.forEach((price) => {
              // Filtrar por recurrencia
              if (
                price.periocity_id.name.toLowerCase() === period.toLowerCase()
              ) {
                // Filtrar por tipo de producto
                const productType = price.product_type_id.name.toLowerCase();
                if (
                  availableTypes.some(
                    (type) => type.toLowerCase() === productType
                  )
                ) {
                  const quantity = getBaseProductQuantity();
                  if (quantity > 0) {
                    addons.push({
                      product,
                      price,
                      quantity,
                    });
                  }
                }
              }
            });
          }
        });

        setAvailableAddons(addons);
      } catch (err) {
        setError('Error al cargar los addons disponibles');
        console.error('Error loading addons:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAddons();
  }, [isOpen, products, period, productsDetails]);

  const handleAddonToggle = (addon: AvailableAddon, isSelected: boolean) => {
    if (isSelected) {
      const selectedAddon: SelectedAddon = {
        priceId: addon.price.stripe_price_id,
        productId: addon.product.id,
        productName: addon.product.title,
        price: addon.price.price,
        additionalPrice: addon.price.additional_price,
        maxAdditional: addon.price.max_additional,
        quantity: addon.quantity,
        productType: addon.price.product_type_id.name,
      };
      setSelectedAddons((prev) => [...prev, selectedAddon]);
    } else {
      setSelectedAddons((prev) =>
        prev.filter(
          (selectedAddon) =>
            selectedAddon.priceId !== addon.price.stripe_price_id
        )
      );
    }
  };

  const calculateAddonPrice = (addon: SelectedAddon) => {
    const { price, additionalPrice, priceId, maxAdditional } = addon;

    // Obtener la cantidad de productos base que el usuario tiene del mismo tipo
    const baseProductQuantity = getBaseProductQuantity();

    if (baseProductQuantity === 0) {
      return 0;
    }

    let totalPrice = 0;

    if (baseProductQuantity < maxAdditional) {
      totalPrice = price * baseProductQuantity;
    } else {
      totalPrice = additionalPrice * baseProductQuantity;
    }

    // Aplicar prorrateo
    const prorrateResult = calculateProrrate({
      price: totalPrice,
      dateExpire,
      typePeriodicity,
    });

    const prorratedPrice = prorrateResult.prorratedPrice;

    // Aplicar descuento del cupón si existe y coincide con el addon
    if (
      appliedCoupon &&
      Array.isArray(appliedCoupon.price) &&
      appliedCoupon.price.some((p) => p.stripe_price_id === priceId)
    ) {
      if (appliedCoupon.is_percent) {
        return prorratedPrice * (1 - appliedCoupon.amount / 100);
      } else {
        return Math.max(0, prorratedPrice - appliedCoupon.amount);
      }
    }

    return prorratedPrice;
  };

  // Función para calcular precio sin cupón (para mostrar en la tarjeta)
  const calculateAddonPriceWithoutCoupon = (addon: SelectedAddon) => {
    const { price, additionalPrice, maxAdditional } = addon;

    const baseProductQuantity = getBaseProductQuantity();

    if (baseProductQuantity === 0) {
      return 0;
    }

    let totalPrice = 0;

    if (baseProductQuantity < maxAdditional) {
      totalPrice = price * baseProductQuantity;
    } else {
      totalPrice = additionalPrice * baseProductQuantity;
    }

    const prorrateResult = calculateProrrate({
      price: totalPrice,
      dateExpire,
      typePeriodicity,
    });

    return prorrateResult.prorratedPrice;
  };

  const isCouponApplicableToAnyAddon = () => {
    if (!appliedCoupon) return true;

    if (!Array.isArray(appliedCoupon.price)) return false;

    const appliesToSelectedAddons = selectedAddons.some((addon) =>
      appliedCoupon.price.some((p) => p.stripe_price_id === addon.priceId)
    );

    return appliesToSelectedAddons;
  };

  const calculateTotal = () => {
    return selectedAddons.reduce(
      (total, addon) => total + calculateAddonPrice(addon),
      0
    );
  };

  // Calcular el total sin descuento del cupón (para mostrar el precio base)
  const calculateTotalWithoutCoupon = () => {
    return selectedAddons.reduce(
      (total, addon) => total + calculateAddonPriceWithoutCoupon(addon),
      0
    );
  };

  const handleConfirm = async () => {
    if (selectedAddons.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const pricesIds = selectedAddons.map((addon) => addon.priceId);
      const request: AddProductsRequest = {
        pricesIds,
        authId: user.uid,
        userImpersonationId: user?.agentAuthData?.userId ?? null,
        couponCode: appliedCoupon?.coupon_code || null,
        userProductId: selectedUserProductId,
      };

      await addProductsToSubscription(subId, request);

      setSelectedAddons([]);
      resetCoupon();
      setSubmitError(null);

      onConfirm(selectedAddons);
    } catch (error) {
      console.error('Error adding addons:', (error as Error).message);
      const errorMessage = (error as Error).message;
      setSubmitError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAddonSelected = (priceId: string) => {
    return selectedAddons.some((addon) => addon.priceId === priceId);
  };

  // Función para limpiar todo cuando se cierra el modal
  const handleClose = () => {
    setSelectedAddons([]);
    resetCoupon();
    setSubmitError(null);
    setCouponNotApplicableError(null);
    onClose();
  };

  // Precio del plan con addons SIN descuento (para mostrar el precio base)
  const newPlanPriceWithoutCoupon =
    currentPlanPrice + calculateTotalWithoutCoupon();
  // Precio con descuento aplicado (para calcular la diferencia a pagar)
  const priceDifference = calculateTotal();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="¡Addons!" size="medium">
      <div className="addon-selection-modal">
        {loading ? (
          <div className="loading">Cargando addons disponibles...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            {/* Sección de impacto financiero prominente */}
            <div className="addon-selection-modal__financial-impact">
              <h2>Selecciona el addon:</h2>

              <div className="addon-selection-tags">
                {availableAddons.length === 0 ? (
                  <div className="no-addons-available">
                    <span className="addon-tag disabled">
                      No hay addons disponibles
                    </span>
                  </div>
                ) : (
                  availableAddons.map((addon) => {
                    const isSelected = isAddonSelected(
                      addon.price.stripe_price_id
                    );
                    const selectedAddon = {
                      priceId: addon.price.stripe_price_id,
                      productId: addon.product.id,
                      productName: addon.price.title,
                      price: addon.price.price,
                      additionalPrice: addon.price.additional_price,
                      maxAdditional: addon.price.max_additional,
                      quantity: addon.quantity,
                      productType: addon.price.product_type_id.name,
                    };
                    const prorratedPrice =
                      calculateAddonPriceWithoutCoupon(selectedAddon);

                    return (
                      <div
                        key={`${addon.product.id}-${addon.price.id}`}
                        className={`addon-tag ${
                          isSelected ? 'addon-tag--selected' : ''
                        }`}
                        onClick={() => handleAddonToggle(addon, !isSelected)}
                      >
                        <span className="addon-tag__name">
                          {addon.price.alias_title.toUpperCase()}
                        </span>
                        <span className="addon-tag__price">
                          {formatCurrency(prorratedPrice)}
                        </span>
                        {isSelected && (
                          <span className="addon-tag__check">✓</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="pricing-breakdown">
                <div className="current-price">
                  <span>Tu plan {period.toLowerCase()} actual:</span>
                  <span className="price-value">
                    {formatCurrency(currentPlanPrice)}
                  </span>
                </div>

                {selectedAddons.length > 0 ? (
                  <div className="pricing-transition pricing-transition--visible">
                    <div className="arrow-down">↓</div>
                    <div className="new-price">
                      <span>
                        Tu siguiente factura{' '}
                        {period.toLowerCase() === 'anual' ? 'anual' : 'mensual'}{' '}
                        con addons:
                      </span>
                      <span className="price-value">
                        {formatCurrency(newPlanPriceWithoutCoupon)}
                      </span>
                    </div>
                    <div className="price-difference">
                      <span>
                        {appliedCoupon && isCouponApplicableToAnyAddon() ? (
                          <>
                            Para que el cambio sea efectivo debe pagar una
                            diferencia de (con cupón aplicado):
                          </>
                        ) : (
                          <>
                            Para que el cambio sea efectivo debe pagar una
                            diferencia de:
                          </>
                        )}
                      </span>
                      <span className="difference-value">
                        {formatCurrency(priceDifference)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="no-addons-selected-message">
                    <div className="no-addons-icon">📋</div>
                    <span className="no-addons-selected-message__text">
                      Selecciona addons para ver el impacto
                    </span>
                  </div>
                )}
              </div>
            </div>

            {submitError && (
              <div className="addon-selection-modal__error">
                <p className="error-message">{submitError}</p>
              </div>
            )}

            {/* Sección de cupón prominente */}
            {selectedAddons.length > 0 && (
              <div className="addon-selection-modal__coupon-section">
                <h3>¿TIENES UN CUPÓN?</h3>
                <div className="addon-selection-modal__coupon">
                  <FormCouponPrice
                    couponCode={couponCode}
                    appliedCoupon={
                      appliedCoupon && isCouponApplicableToAnyAddon()
                        ? appliedCoupon
                        : null
                    }
                    couponError={couponError || couponNotApplicableError}
                    handleCouponCode={handleCouponCode}
                    isAnyProductSelected={selectedAddons.length > 0}
                    resetCoupon={resetCoupon}
                    handleApplyCoupon={handleApplyCoupon}
                    loadingCoupon={loadingCoupon}
                  />
                </div>
              </div>
            )}

            <div className="addon-selection-modal__actions">
              <Button
                type="button"
                template="secondary"
                typeStyle="square"
                color="primary"
                size="medium"
                onClick={handleClose}
              >
                SALIR
              </Button>
              <Button
                type="button"
                template="primary"
                typeStyle="square"
                color="primary"
                size="medium"
                onClick={handleConfirm}
                isDisabled={
                  selectedAddons.length === 0 || isLoading || isSubmitting
                }
              >
                {isSubmitting
                  ? 'PROCESANDO...'
                  : isLoading
                  ? 'PROCESANDO...'
                  : 'REALIZAR PAGO'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
